import {
  normalizeLocaleNumberString,
  normalizePctInputToRate,
  parseLocaleNumber,
} from "@/lib/businessRules";

export { normalizeLocaleNumberString, parseLocaleNumber };

/**
 * Admin Pilotage display conventions
 * - Missing value (null/undefined/NaN/empty): "—"
 * - Currency: French locale with "€", no decimals by default
 * - KPI percentages: 0-1 decimal
 * - Detailed percentages (tables): fixed 2 decimals
 * - Counts: integer
 * - Business decimals (ETP/ratios): fixed 2 decimals
 */
export const PILOTAGE_PLACEHOLDER = "—";

const monthFormatter = new Intl.DateTimeFormat("fr-FR", {
  month: "long",
  year: "numeric",
});

const shortDateFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const defaultCurrencyFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};
const defaultPercentFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
};
const integerFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};
const defaultNumberFormatOptions = {
  maximumFractionDigits: 0,
};
const pilotagePercentKpiOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
};
const pilotagePercentDetailOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};
const pilotageDecimalOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

const pad = (value) => String(value).padStart(2, "0");

const toFiniteNumericValue = (value) => {
  return parseLocaleNumber(value);
};
const normalizeSpacing = (value) => String(value).replace(/[\u00a0\u202f]/g, " ");

export const TACE_LABEL = "TACE (taux d'activité cible estimé)";
export const INTERMEDIARY_MARGIN_LABEL = "Marge intermédiaire (%)";
export const CONSULTANT_RATE_LABEL = "CJM";
export const MISSION_TYPES = ["FREELANCE", "CDI"];
export const percentToRatio = (value) => {
  const numericValue = toFiniteNumericValue(value);
  return numericValue === null ? null : numericValue / 100;
};
export const ratioToPercent = (value) => {
  const numericValue = toFiniteNumericValue(value);
  return numericValue === null ? null : numericValue * 100;
};

export const toMonthKey = (dateValue) => {
  const nextDate = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
  if (Number.isNaN(nextDate.getTime())) {
    return "";
  }

  return `${nextDate.getFullYear()}-${pad(nextDate.getMonth() + 1)}`;
};

export const formatMonthLabel = (monthKey) => {
  if (!monthKey) {
    return "—";
  }

  const parsedDate = new Date(`${monthKey}-01T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return monthKey;
  }

  return monthFormatter.format(parsedDate);
};

export const buildMonthWindow = (radius = 6, referenceDate = new Date()) => {
  const baseDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const currentMonthKey = toMonthKey(baseDate);

  return Array.from({ length: radius * 2 + 1 }, (_, index) => {
    const offset = index - radius;
    const monthDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + offset, 1);
    const key = toMonthKey(monthDate);

    return {
      key,
      label: monthFormatter.format(monthDate),
      isFuture: key > currentMonthKey,
      isPast: key < currentMonthKey,
      isCurrent: key === currentMonthKey,
    };
  });
};

export const buildMissionMonthWindow = (startDateValue, endDateValue) => {
  const parsedStartDate = new Date(startDateValue);
  if (Number.isNaN(parsedStartDate.getTime())) {
    return [];
  }

  const startMonthDate = new Date(parsedStartDate.getFullYear(), parsedStartDate.getMonth(), 1);
  const parsedEndDate = endDateValue ? new Date(endDateValue) : null;
  const fallbackEndDate = new Date(
    startMonthDate.getFullYear(),
    startMonthDate.getMonth() + 12,
    1,
  );
  const endMonthDate =
    parsedEndDate && !Number.isNaN(parsedEndDate.getTime())
      ? new Date(parsedEndDate.getFullYear(), parsedEndDate.getMonth(), 1)
      : fallbackEndDate;
  const currentMonthKey = toMonthKey(new Date());
  const totalMonths =
    (endMonthDate.getFullYear() - startMonthDate.getFullYear()) * 12 +
    (endMonthDate.getMonth() - startMonthDate.getMonth());

  return Array.from({ length: totalMonths + 1 }, (_, index) => {
    const monthDate = new Date(
      startMonthDate.getFullYear(),
      startMonthDate.getMonth() + index,
      1,
    );
    const key = toMonthKey(monthDate);

    return {
      key,
      label: monthFormatter.format(monthDate),
      isFuture: key > currentMonthKey,
      isPast: key < currentMonthKey,
      isCurrent: key === currentMonthKey,
    };
  });
};

export const formatCurrency = (value, options = defaultCurrencyFormatOptions) => {
  if (value === null || value === undefined || value === "") {
    return PILOTAGE_PLACEHOLDER;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return PILOTAGE_PLACEHOLDER;
  }

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    ...defaultCurrencyFormatOptions,
    ...options,
  }).format(numericValue);
};

export const formatNumber = (value, options = defaultNumberFormatOptions) => {
  const numericValue = toFiniteNumericValue(value);
  if (numericValue === null) {
    return PILOTAGE_PLACEHOLDER;
  }

  return normalizeSpacing(
    new Intl.NumberFormat("fr-FR", {
      ...defaultNumberFormatOptions,
      ...options,
    }).format(numericValue),
  );
};

export const formatEUR0 = (value) => {
  const numericValue = toFiniteNumericValue(value);
  if (numericValue === null) {
    return PILOTAGE_PLACEHOLDER;
  }

  return `${formatNumber(numericValue, integerFormatOptions)} €`;
};

export const formatEUR2 = (value) => {
  const numericValue = toFiniteNumericValue(value);
  if (numericValue === null) {
    return PILOTAGE_PLACEHOLDER;
  }

  return `${formatNumber(numericValue, pilotageDecimalOptions)} €`;
};

export const formatDateLabel = (value) => {
  if (!value) {
    return PILOTAGE_PLACEHOLDER;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return PILOTAGE_PLACEHOLDER;
  }

  return shortDateFormatter.format(parsedDate);
};

export const normalizePercentValue = (value) => {
  const numericValue = toFiniteNumericValue(value);
  if (numericValue === null) {
    return null;
  }

  return Math.abs(numericValue) <= 1 ? ratioToPercent(numericValue) : numericValue;
};

export const formatPercentValue = (value, options = defaultPercentFormatOptions) => {
  const normalizedValue = normalizePercentValue(value);

  if (normalizedValue === null) {
    return PILOTAGE_PLACEHOLDER;
  }

  return `${new Intl.NumberFormat("fr-FR", {
    ...defaultPercentFormatOptions,
    ...options,
  }).format(normalizedValue)}%`;
};

export const formatPct0 = (value) => {
  const normalizedValue = normalizePercentValue(value);

  if (normalizedValue === null) {
    return PILOTAGE_PLACEHOLDER;
  }

  const roundedValue = Math.round((normalizedValue + Number.EPSILON) * 100) / 100;

  return `${formatNumber(roundedValue, integerFormatOptions)} %`;
};

export const formatPilotageCurrency = (value, options = integerFormatOptions) =>
  formatCurrency(value, {
    ...integerFormatOptions,
    ...options,
  });

export const formatPilotageCount = (value, options = integerFormatOptions) =>
  formatNumber(value, {
    ...integerFormatOptions,
    ...options,
  });

export const formatPilotageDecimal = (value, options = pilotageDecimalOptions) =>
  formatNumber(value, {
    ...pilotageDecimalOptions,
    ...options,
  });

const formatPilotagePercent = (value, options) => {
  const normalizedValue = normalizePercentValue(value);

  if (normalizedValue === null || !Number.isFinite(normalizedValue)) {
    return PILOTAGE_PLACEHOLDER;
  }

  return `${formatNumber(normalizedValue, options)} %`;
};

export const formatPilotagePercentKpi = (value, options = pilotagePercentKpiOptions) =>
  formatPilotagePercent(value, {
    ...pilotagePercentKpiOptions,
    ...options,
  });

export const formatPilotagePercentDetail = (value, options = pilotagePercentDetailOptions) =>
  formatPilotagePercent(value, {
    ...pilotagePercentDetailOptions,
    ...options,
  });

export const formatPilotageSignedDelta = (
  value,
  formatter = (absoluteValue) => formatPilotageCurrency(absoluteValue),
) => {
  const numericValue = toFiniteNumericValue(value);

  if (numericValue === null) {
    return PILOTAGE_PLACEHOLDER;
  }

  const formattedAbsolute = formatter(Math.abs(numericValue));
  if (formattedAbsolute === PILOTAGE_PLACEHOLDER) {
    return PILOTAGE_PLACEHOLDER;
  }

  if (numericValue > 0) {
    return `+${formattedAbsolute}`;
  }
  if (numericValue < 0) {
    return `-${formattedAbsolute}`;
  }
  return formattedAbsolute;
};

export const formatPct2 = (value) => {
  return formatPilotagePercentDetail(value);
};

export const formatPercentInputValue = (value) => {
  const normalizedValue = normalizePercentValue(value);

  if (normalizedValue === null) {
    return "";
  }

  return String(normalizedValue);
};

export const parsePercentInputToRatio = (value, fallback = null) => {
  const ratioValue = normalizePctInputToRate(value);
  return ratioValue === null ? fallback : ratioValue;
};

export const clampNumberInput = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  const numericValue = parseLocaleNumber(value);
  if (!Number.isFinite(numericValue)) {
    return "";
  }

  return numericValue;
};

const toFiniteNumber = (value) => toFiniteNumericValue(value);

export const normalizeMissionType = (value) => {
  const normalizedValue = String(value || "").trim().toUpperCase();
  return MISSION_TYPES.includes(normalizedValue) ? normalizedValue : null;
};

const inferMissionTypeFromFinancials = (
  mission,
  { logWarnings = false, logger = console } = {},
) => {
  const hasSalary = (toFiniteNumber(mission?.salaire_annuel) || 0) > 0;
  const hasConsultantRate = (toFiniteNumber(mission?.tjm_consultant) || 0) > 0;

  if (hasSalary && hasConsultantRate && logWarnings && typeof logger?.warn === "function") {
    logger.warn(
      "Mission has both salaire_annuel and tjm_consultant; mission type is ambiguous and the hidden field will be cleared on save.",
      mission,
    );
  }

  if (hasSalary) {
    return hasConsultantRate ? null : "CDI";
  }
  if (hasConsultantRate) {
    return "FREELANCE";
  }

  return null;
};

export const resolveCollaboratorContractType = (collaborator) => {
  const kind =
    collaborator?.contract_type ??
    collaborator?.contractType ??
    collaborator?.contract_kind ??
    collaborator?.contractKind;
  if (!kind) return null;
  const normalized = String(kind).trim().toUpperCase();
  if (["CDI", "CDD", "ALTERNANT"].includes(normalized)) {
    return "CDI";
  }
  if (["FREELANCE", "PORTAGE", "SUB_CONTRACTOR"].includes(normalized)) {
    return "FREELANCE";
  }
  return null;
};

export const calculateSeniority = (startDate, endDate) => {
  if (!startDate) return "—";
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return "—";
  const end = endDate ? new Date(endDate) : new Date();
  if (Number.isNaN(end.getTime())) return "—";

  const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  return diffMonths >= 0 ? `${diffMonths} mois` : "0 mois";
};

export const getResourceTypeLabel = (contractKind) => {
  const kind = String(contractKind || "").toUpperCase();
  if (["CDI", "CDD", "ALTERNANT"].includes(kind)) {
    return "Salarié";
  }
  if (kind === "FREELANCE") {
    return "Freelance";
  }
  if (kind === "PORTAGE") {
    return "Porté";
  }
  if (kind === "SUB_CONTRACTOR") {
    return "Sous-traitant";
  }
  return "Autre";
};

export const getContractKindLabel = (contractKind) => {
  const kind = String(contractKind || "").toUpperCase();
  switch (kind) {
    case "CDI":
      return "CDI";
    case "CDD":
      return "CDD";
    case "ALTERNANT":
      return "Alternance";
    case "FREELANCE":
      return "Freelance";
    case "PORTAGE":
      return "Portage";
    case "SUB_CONTRACTOR":
      return "Sous-traitance";
    default:
      return contractKind || "—";
  }
};

export const getCollaboratorStatusLabel = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "RECRUITMENT":
      return "En recrutement";
    case "PLANNED":
      return "Prévu";
    case "ACTIVE":
      return "Actif";
    case "NOTICE":
      return "En préavis";
    case "LEFT":
      return "Sorti";
    case "INACTIVE":
      return "Inactif";
    default:
      return status || "—";
  }
};

export const OPERATIONAL_STATUS = {
  EN_MISSION: "EN_MISSION",
  INTERCONTRAT_SALARIE: "INTERCONTRAT_SALARIE",
  SANS_MISSION_FREELANCE: "SANS_MISSION_FREELANCE",
  NON_DEMARRE: "NON_DEMARRE",
  CONTRAT_TERMINE: "CONTRAT_TERMINE",
  INACTIF: "INACTIF",
};

export const getOperationalStatusLabel = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "EN_MISSION":
    case "EN MISSION":
      return "En mission";
    case "INTERCONTRAT_SALARIE":
    case "INTERCONTRAT":
      return "Intercontrat salarié";
    case "SANS_MISSION_FREELANCE":
    case "SANS MISSION":
    case "DISPONIBLE":
      return "Freelance sans mission";
    case "NON_DEMARRE":
    case "NON DÉMARRÉ":
    case "PRÉVU":
    case "RECRUITMENT":
      return "Prise de poste à venir";
    case "CONTRAT_TERMINE":
    case "CONTRAT TERMINÉ":
    case "SORTI":
      return "Contrat terminé";
    case "INACTIF":
    case "INACTIVE":
      return "Inactif";
    default:
      return status || "—";
  }
};

export const getOperationalStatusBadgeClass = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "EN_MISSION":
    case "EN MISSION":
      return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    case "INTERCONTRAT_SALARIE":
    case "INTERCONTRAT":
      return "bg-amber-500/10 text-amber-400 border-amber-500/20";
    case "SANS_MISSION_FREELANCE":
    case "SANS MISSION":
    case "DISPONIBLE":
      return "bg-sky-500/10 text-sky-400 border-sky-500/20";
    case "NON_DEMARRE":
    case "NON DÉMARRÉ":
      return "bg-indigo-500/10 text-indigo-400 border-indigo-500/20";
    case "CONTRAT_TERMINE":
    case "CONTRAT TERMINÉ":
    case "SORTI":
    case "INACTIF":
    case "INACTIVE":
      return "bg-slate-800 text-slate-400 border-slate-700/50";
    default:
      return "bg-slate-800 text-slate-400 border-white/10";
  }
};

export const resolveCollaboratorOperationalStatus = (
  collaborator,
  missions = [],
  referenceDate = new Date(),
) => {
  if (!collaborator) {
    return {
      status: OPERATIONAL_STATUS.INACTIF,
      label: getOperationalStatusLabel(OPERATIONAL_STATUS.INACTIF),
      reason: "Aucun collaborateur fourni",
      isSalaried: false,
      isIntercontract: false,
    };
  }

  const kind = String(collaborator.contract_kind || collaborator.contract_type || "").toUpperCase();
  const isSalaried = !["FREELANCE", "PORTAGE", "SUB_CONTRACTOR"].includes(kind);
  const collabStatus = String(collaborator.status || "ACTIVE").toUpperCase();

  const toDateObj = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d;
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const ref = referenceDate instanceof Date ? referenceDate : toDateObj(referenceDate) || new Date();
  const refIso = ref.toISOString().substring(0, 10);

  const startIso = collaborator.employment_start_date || collaborator.entry_date || null;
  const endIso = collaborator.employment_end_date || collaborator.exit_date || null;

  // 1. Sortie passée
  if (endIso && endIso < refIso) {
    return {
      status: OPERATIONAL_STATUS.CONTRAT_TERMINE,
      label: getOperationalStatusLabel(OPERATIONAL_STATUS.CONTRAT_TERMINE),
      reason: `Contrat terminé le ${endIso}`,
      isSalaried,
      isIntercontract: false,
    };
  }

  if (["LEFT", "INACTIVE"].includes(collabStatus) && (!endIso || endIso < refIso)) {
    return {
      status: OPERATIONAL_STATUS.CONTRAT_TERMINE,
      label: getOperationalStatusLabel(OPERATIONAL_STATUS.CONTRAT_TERMINE),
      reason: `Statut administratif ${collabStatus}`,
      isSalaried,
      isIntercontract: false,
    };
  }

  // 2. Entrée future
  if (startIso && startIso > refIso) {
    return {
      status: OPERATIONAL_STATUS.NON_DEMARRE,
      label: getOperationalStatusLabel(OPERATIONAL_STATUS.NON_DEMARRE),
      reason: `Prise de poste prévue le ${startIso}`,
      isSalaried,
      isIntercontract: false,
    };
  }

  // 3. Missions
  const collabId = String(collaborator.id || collaborator._id || "");
  const collabEmail = String(collaborator.email || "").toLowerCase();

  const relevantMissions = (Array.isArray(missions) ? missions : []).filter((m) => {
    const mCollabId = String(m.collaborator_id || m.consultant_id || "");
    const mCollabEmail = String(m.collaborator_email || m.consultant_email || "").toLowerCase();
    return (collabId && mCollabId === collabId) || (collabEmail && mCollabEmail === collabEmail);
  });

  const activeMissionsAtRef = relevantMissions.filter((m) => {
    const mStatus = String(m.status || "ACTIVE").toUpperCase();
    if (["CANCELLED", "ABANDONED", "REJECTED"].includes(mStatus)) return false;
    const mStart = m.start_date || null;
    const mEnd = m.end_date || null;
    const isTemporallyCovering = (!mStart || mStart <= refIso) && (!mEnd || mEnd >= refIso);
    return isTemporallyCovering && ["ACTIVE", "IN_PROGRESS", "SIGNED", "CONFIRMED", "OPEN"].includes(mStatus);
  });

  if (activeMissionsAtRef.length > 0) {
    const activeM = activeMissionsAtRef[0];
    const clientName = activeM.client_name || activeM.client || "Client";
    return {
      status: OPERATIONAL_STATUS.EN_MISSION,
      label: getOperationalStatusLabel(OPERATIONAL_STATUS.EN_MISSION),
      reason: `En mission chez ${clientName}`,
      isSalaried,
      isIntercontract: false,
      activeMission: activeM,
      activeMissionsCount: activeMissionsAtRef.length,
    };
  }

  // 4. Sans mission active
  if (!isSalaried) {
    return {
      status: OPERATIONAL_STATUS.SANS_MISSION_FREELANCE,
      label: getOperationalStatusLabel(OPERATIONAL_STATUS.SANS_MISSION_FREELANCE),
      reason: "Freelance sans mission active (disponible)",
      isSalaried: false,
      isIntercontract: false,
      activeMissionsCount: 0,
    };
  }

  return {
    status: OPERATIONAL_STATUS.INTERCONTRAT_SALARIE,
    label: getOperationalStatusLabel(OPERATIONAL_STATUS.INTERCONTRAT_SALARIE),
    reason: "Collaborateur salarié en intercontrat",
    isSalaried: true,
    isIntercontract: true,
    activeMissionsCount: 0,
  };
};

export const resolveMissionType = (
  mission,
  { logWarnings = false, logger = console } = {},
) => {
  const explicitType = normalizeMissionType(mission?.type);
  if (explicitType) {
    return explicitType;
  }

  const inferredType = inferMissionTypeFromFinancials(mission, { logWarnings, logger });
  if (inferredType) {
    return inferredType;
  }

  const hasSalary = (toFiniteNumber(mission?.salaire_annuel) || 0) > 0;
  const hasConsultantRate = (toFiniteNumber(mission?.tjm_consultant) || 0) > 0;

  if (hasSalary && hasConsultantRate) {
    return "CDI";
  }

  return "FREELANCE";
};

export const resolveMissionTypeWithCollaborator = (
  mission,
  collaborator,
  { logWarnings = false, logger = console } = {},
) => {
  const explicitType = normalizeMissionType(mission?.type);
  if (explicitType) {
    return explicitType;
  }

  const inferredType = inferMissionTypeFromFinancials(mission, { logWarnings, logger });
  if (inferredType) {
    return inferredType;
  }

  const collaboratorContractType = resolveCollaboratorContractType(collaborator);
  if (collaboratorContractType) {
    return collaboratorContractType;
  }

  const hasSalary = (toFiniteNumber(mission?.salaire_annuel) || 0) > 0;
  const hasConsultantRate = (toFiniteNumber(mission?.tjm_consultant) || 0) > 0;

  if (hasSalary && hasConsultantRate) {
    return "CDI";
  }

  return "FREELANCE";
};

export const DEFAULT_EMPLOYER_COST_COEFFICIENT = 1.5;

export const resolveEmployerCostCoefficient = (mission) => {
  const coeff = Number(mission?.employer_cost_coefficient);
  return Number.isFinite(coeff) && coeff > 0 ? coeff : DEFAULT_EMPLOYER_COST_COEFFICIENT;
};

export const normalizeMissionFormForType = (formValues, nextType) => {
  const missionType = normalizeMissionType(nextType) || "FREELANCE";

  if (missionType === "CDI") {
    return {
      ...formValues,
      type: missionType,
      tjm_consultant: "",
      employer_cost_coefficient: formValues.employer_cost_coefficient || "1.50",
    };
  }

  return {
    ...formValues,
    type: missionType,
    salaire_annuel: "",
    employer_cost_coefficient: "",
  };
};

export const buildMissionPayload = (formValues) => {
  const missionType = resolveMissionType(formValues);
  const tarifClient = parseLocaleNumber(formValues.tarif_client) ?? 0;

  const marginMode = formValues.intermediaire_margin_mode || "PERCENT";
  let marginPct;
  let marginAmount;

  if (marginMode === "AMOUNT") {
    marginAmount = parseLocaleNumber(formValues.intermediaire_margin_amount) ?? 0;
    marginPct = tarifClient > 0 ? marginAmount / tarifClient : 0;
  } else {
    marginPct = parsePercentInputToRatio(formValues.intermediaire_margin_pct, 0);
    marginAmount = tarifClient * marginPct;
  }

  const normalizedRateChanges = Array.isArray(formValues.rate_changes)
    ? formValues.rate_changes.map((rc) => {
        const rawVal = rc.value !== undefined && rc.value !== null ? rc.value : rc.new_value;
        const numVal = parseLocaleNumber(rawVal);
        return {
          ...(rc.id ? { id: rc.id } : {}),
          field: rc.field === "salary" ? "annual_salary" : rc.field,
          effective_date: rc.effective_date,
          value: numVal !== null && !isNaN(numVal) ? numVal : 0,
          comment: rc.comment?.trim() || null,
        };
      })
    : [];

  const payload = {
    type: missionType,
    collaborator_id: formValues.collaborator_id,
    client_id: formValues.client_id,
    start_date: formValues.start_date,
    end_date: formValues.end_date || null,
    status: formValues.status,
    tarif_client: tarifClient,
    tjm_consultant: toFiniteNumber(formValues.tjm_consultant),
    salaire_annuel: toFiniteNumber(formValues.salaire_annuel),
    tace: parsePercentInputToRatio(formValues.tace, 0.8),
    intermediaire_margin_mode: marginMode,
    intermediaire_margin_pct: marginPct,
    intermediaire_margin_amount: marginAmount,
    mission_role: formValues.mission_role || null,
    client_free_days_granted: Math.max(0, parseLocaleNumber(formValues.client_free_days_granted) ?? 0),
    rate_changes: normalizedRateChanges,
  };

  if (missionType === "CDI") {
    return {
      ...payload,
      tjm_consultant: null,
      employer_cost_coefficient: parseLocaleNumber(formValues.employer_cost_coefficient) ?? 1.5,
    };
  }

  return {
    ...payload,
    salaire_annuel: null,
    employer_cost_coefficient: null,
  };
};

export const formatDays = (value, fallback = PILOTAGE_PLACEHOLDER) => {
  const numericValue = toFiniteNumericValue(value);
  if (numericValue === null) {
    return fallback;
  }
  const formatted = numericValue.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${normalizeSpacing(formatted)} j`;
};

export const calculateRemainingFreeDays = (granted, consumed) => {
  const g = toFiniteNumericValue(granted) ?? 0;
  const c = toFiniteNumericValue(consumed) ?? 0;
  return Math.max(0, g - c);
};

export const calculateBilledDays = (worked, freeDays) => {
  const w = toFiniteNumericValue(worked);
  if (w === null) {
    return null;
  }
  const f = toFiniteNumericValue(freeDays) ?? 0;
  return Math.max(0, w - f);
};

export const getObjectiveStatus = (actual, target) => {
  if (actual === null || actual === undefined || target === null || target === undefined) {
    return {
      status: "in_progress",
      label: PILOTAGE_PLACEHOLDER,
      progress: 0,
    };
  }

  const actVal = Number(actual);
  const tgtVal = Number(target);

  if (Number.isNaN(actVal) || Number.isNaN(tgtVal)) {
    return {
      status: "in_progress",
      label: PILOTAGE_PLACEHOLDER,
      progress: 0,
    };
  }

  if (actVal > tgtVal) {
    return {
      status: "exceeded",
      label: "Objectif dépassé",
      progress: 100,
    };
  }

  if (actVal === tgtVal) {
    return {
      status: "achieved",
      label: "Objectif atteint",
      progress: 100,
    };
  }

  // actVal < tgtVal
  if (tgtVal < 0) {
    return {
      status: "in_progress",
      label: "En retard",
      progress: 0,
    };
  }

  const calculatedPercent = tgtVal !== 0 ? (actVal / tgtVal) * 100 : 0;
  return {
    status: "in_progress",
    label: `${Math.round(calculatedPercent)} %`,
    progress: Math.min(Math.max(calculatedPercent, 0), 100),
  };
};
